/* RESPONSIBLE TEAM: team-inbox-experience */
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

/**
 * Multi-level priority constants.
 *
 * `PRIORITY` is an alias for `URGENT` (both = 64) preserved for binary-intent
 * call sites — the legacy "Mark as priority" UX has no level concept, and
 * naming the value `PRIORITY` at those sites reads more accurately than
 * naming it `URGENT`. Mirrors the backend Rails enum (`MessageThread.priorities`
 * keeps `:priority` as the canonical key, `:urgent` as the API-V3 alias) and
 * the React `PriorityValue` enum (`Priority` + `Urgent` both = 64).
 */
export const PRIORITY_LEVELS = {
  NONE: 0,
  LOW: 16,
  MEDIUM: 32,
  HIGH: 48,
  URGENT: 64,
  PRIORITY: 64,
} as const;

export type PriorityLevel = (typeof PRIORITY_LEVELS)[keyof typeof PRIORITY_LEVELS];

// Accepts boolean (legacy callers / mirage seeds) or number; coerces to integer level.
export function normalizePriority(value: boolean | number | undefined | null): number {
  if (typeof value === 'boolean') {
    return value ? PRIORITY_LEVELS.PRIORITY : PRIORITY_LEVELS.NONE;
  }
  return value ?? PRIORITY_LEVELS.NONE;
}

// Stable display order for the picker, top → bottom: most-urgent first, "None"
// last. The backend allows zero/64 binary writes when the flag is OFF, but the
// picker is only mounted when the flag is ON, so all five levels are listed.
export const PRIORITY_LEVEL_PICKER_ORDER = [
  PRIORITY_LEVELS.URGENT,
  PRIORITY_LEVELS.HIGH,
  PRIORITY_LEVELS.MEDIUM,
  PRIORITY_LEVELS.LOW,
  PRIORITY_LEVELS.NONE,
] as const;

// Backend-canonical level names. Matches `Inbox::Helpers::PriorityLevels.name_for`
// on the Rails side (the API-V3 names: "none", "low", "medium", "high", "urgent").
// Used as the i18n key suffix and as the API string sent to `set_priority_level`.
export type PriorityLevelName = 'none' | 'low' | 'medium' | 'high' | 'urgent';

const NAME_BY_VALUE: Record<number, PriorityLevelName> = {
  [PRIORITY_LEVELS.NONE]: 'none',
  [PRIORITY_LEVELS.LOW]: 'low',
  [PRIORITY_LEVELS.MEDIUM]: 'medium',
  [PRIORITY_LEVELS.HIGH]: 'high',
  [PRIORITY_LEVELS.URGENT]: 'urgent',
};

export function priorityLevelName(value: number): PriorityLevelName {
  return NAME_BY_VALUE[value] ?? 'none';
}

// Visual metadata per level. Dedicated priority icons from Pulse (synced via
// Surge PR #126940): `urgent-priority`, `high-priority`, `medium-priority`,
// `low-priority`. None keeps `subtract` (minus) — no dedicated none-priority
// icon exists. Lower levels (Low, None) are muted via `iconClass`.
export interface PriorityLevelDisplay {
  value: number;
  name: PriorityLevelName;
  icon: InterfaceIconName;
  iconClass?: string;
}

const DISPLAY_BY_VALUE: Record<number, PriorityLevelDisplay> = {
  [PRIORITY_LEVELS.URGENT]: {
    value: PRIORITY_LEVELS.URGENT,
    name: 'urgent',
    icon: 'urgent-priority',
  },
  [PRIORITY_LEVELS.HIGH]: {
    value: PRIORITY_LEVELS.HIGH,
    name: 'high',
    icon: 'high-priority',
  },
  [PRIORITY_LEVELS.MEDIUM]: {
    value: PRIORITY_LEVELS.MEDIUM,
    name: 'medium',
    icon: 'medium-priority',
  },
  [PRIORITY_LEVELS.LOW]: {
    value: PRIORITY_LEVELS.LOW,
    name: 'low',
    icon: 'low-priority',
    iconClass: 'text-muted',
  },
  [PRIORITY_LEVELS.NONE]: {
    value: PRIORITY_LEVELS.NONE,
    name: 'none',
    icon: 'subtract',
    iconClass: 'text-muted',
  },
};

export function priorityLevelDisplay(value: number): PriorityLevelDisplay {
  return DISPLAY_BY_VALUE[value] ?? DISPLAY_BY_VALUE[PRIORITY_LEVELS.NONE];
}

export function priorityLevelOptions(): PriorityLevelDisplay[] {
  return PRIORITY_LEVEL_PICKER_ORDER.map((v) => priorityLevelDisplay(v));
}
